import {
  HomepageCarouselConfig,
  HomepageCarouselWeblabConfig,
  SUPPORTED_HOMEPAGE_QUERY_TYPES,
} from 'src/constants/homepageCarousels';
import { WeblabTreatment } from 'src/constants/weblab';

/**
 * Groups items into chunks for carousel display
 *
 * @param items Items to group
 * @param groupSize Items per group
 * @returns Array of item groups
 */
export const groupItemsForCarousel = <T>(items: T[], groupSize: number): T[][] => {
  const groups: T[][] = [];
  for (let i = 0; i < items.length; i += groupSize) {
    groups.push(items.slice(i, i + groupSize));
  }
  return groups;
};

/**
 * Determines if carousel navigation should be disabled
 *
 * @param itemCount Total number of items
 * @param minItemsForNav Minimum items required for navigation
 * @returns Boolean indicating if navigation should be disabled
 */
export const shouldDisableNavigation = (itemCount: number, minItemsForNav: number = 3): boolean => {
  return itemCount < minItemsForNav;
};

/**
 * Creates a unique key for a group of items
 *
 * @param group Group of items with ID property
 * @param idField Field name for the ID property (defaults to 'id')
 * @returns String key for the group
 */
export const createGroupKey = <T extends Record<string, any>>(
  group: T[],
  idField: keyof T = 'id',
): string => {
  return group.map((item) => item[idField]).join('-');
};

/**
 * Validates carousel configuration based on query type
 * Handles null/undefined config and validates query type and required parameters
 *
 * @param config The carousel configuration to validate
 * @returns Type predicate indicating if the configuration is valid and narrows type
 */
export const validateCarouselConfig = (
  config: HomepageCarouselConfig | undefined,
): config is HomepageCarouselConfig => {
  // Handle null/undefined config
  if (!config) {
    return false;
  }

  // Check if query type is supported
  if (!Object.values(SUPPORTED_HOMEPAGE_QUERY_TYPES).includes(config.queryType)) {
    // Unsupported type - return false
    return false;
  }

  // Type-specific validation
  if (config.queryType === SUPPORTED_HOMEPAGE_QUERY_TYPES.TAGS) {
    // For TAGS type, validate tagIds exist and not empty
    if (!config.tagIds || config.tagIds.length === 0) {
      return false;
    }
  }

  // Future query types will automatically proceed to next step
  // if (config.queryType === SUPPORTED_HOMEPAGE_QUERY_TYPES.POPULAR) {
  //   // Validate popular-specific requirements
  // }

  return true;
};

/**
 * Validates if a carousel should be shown based on its weblab configuration
 * and the user's current weblab treatment.
 *
 * @param weblabConfig - Optional weblab configuration from the carousel config
 * @param userTreatment - The user's current treatment for the weblab (undefined if weblab not found)
 * @returns true if carousel should be shown, false otherwise
 */
export const validateCarouselWeblab = (
  weblabConfig: HomepageCarouselWeblabConfig | undefined,
  userTreatment: WeblabTreatment | undefined,
): boolean => {
  // No weblab config means no gating — always show
  if (!weblabConfig) {
    return true;
  }

  // Weblab configured but no treatment found for user — don't show
  if (!userTreatment) {
    return false;
  }

  // Check if user's treatment is in the allowed treatments list
  return weblabConfig.treatments.includes(userTreatment);
};
